import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { Btn, H5, P } from "../../AbstractElements";
import { fetchRequisites, saveRequisites } from "../../helpers/documentsApi";

// Нумерация типов контрагента с бэкенда (common\models\document\DocumentRequisites).
// Одним объектом, а не четырьмя константами: PERSON и SELF_EMPLOYED в коде
// формы не упоминаются — у них нет своих полей, — и по отдельности eslint
// справедливо ругался на них как на мёртвые. Терять нумерацию при этом нельзя.
const TYPE = {
  PERSON: 1,
  SELF_EMPLOYED: 2,
  ENTREPRENEUR: 3,
  COMPANY: 4,
};

const EMPTY = {
  type: TYPE.COMPANY,
  name: "",
  inn: "",
  kpp: "",
  ogrn: "",
  legal_address: "",
  postal_address: "",
  bank_name: "",
  bank_bik: "",
  bank_account: "",
  bank_corr_account: "",
  signer_name: "",
  signer_position: "",
  signer_basis: "",
  email: "",
  phone: "",
};

/** Какие поля показывать для выбранного типа контрагента. */
const isVisible = (field, type) => {
  if (field === "kpp") return type === TYPE.COMPANY;
  if (field === "ogrn") return type === TYPE.ENTREPRENEUR || type === TYPE.COMPANY;
  if (["signer_name", "signer_position", "signer_basis"].includes(field)) {
    return type === TYPE.COMPANY;
  }
  return true;
};

const nameLabel = (type) =>
  type === TYPE.COMPANY ? "Наименование организации" : "Фамилия, имя, отчество";

const RequisitesForm = ({ companyId, onSaved }) => {
  const [types, setTypes] = useState([]);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchRequisites(companyId);
        if (cancelled) return;
        setTypes(data.types || []);
        if (data.values) {
          setValues({ ...EMPTY, ...data.values, type: Number(data.values.type) });
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    setErrors({});

    try {
      await saveRequisites(values, companyId);
      setMessage("Реквизиты сохранены.");
      if (onSaved) onSaved(values);
    } catch (e) {
      if (e.errors) {
        setErrors(e.errors);
        setError("Проверьте выделенные поля.");
      } else {
        setError(e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, extra = {}) => {
    if (!isVisible(name, Number(values.type))) return null;
    const fieldErrors = errors[name];

    return (
      <Col md={extra.wide ? 12 : 6} key={name}>
        <FormGroup>
          <Label className="col-form-label">{label}</Label>
          <Input
            type={extra.type || "text"}
            className={fieldErrors ? "is-invalid" : ""}
            value={values[name] || ""}
            placeholder={extra.placeholder || ""}
            onChange={(e) => setField(name, e.target.value)}
          />
          {fieldErrors && (
            <div className="invalid-feedback d-block">{fieldErrors.join(" ")}</div>
          )}
        </FormGroup>
      </Col>
    );
  };

  if (loading) {
    return <P>Загрузка…</P>;
  }

  // Без справочника типов контрагента форма бесполезна: селект «Кто вы»
  // отрисуется пустым, и человек решит, что сломан он, а не запрос к API.
  // Показываем причину вместо формы, которая всё равно не сохранится.
  if (!types.length) {
    return (
      <Card>
        <CardHeader className="pb-0">
          <H5>Реквизиты</H5>
        </CardHeader>
        <CardBody>
          <P className="text-danger">
            {error || "Справочник типов контрагента не загрузился."}
          </P>
          <P className="text-muted mb-0">
            Форма скрыта, пока не отвечает API: заполнять её сейчас бессмысленно —
            реквизиты не сохранятся.
          </P>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-0">
        <H5>Реквизиты</H5>
        <P className="text-muted mb-0">
          Заполняются один раз. Дальше подставляются во все документы автоматически.
        </P>
      </CardHeader>
      <CardBody>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className="col-form-label">Кто вы</Label>
                <Input
                  type="select"
                  value={values.type}
                  onChange={(e) => setField("type", Number(e.target.value))}
                >
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            {field("name", nameLabel(Number(values.type)))}
            {field("inn", "ИНН")}
            {field("kpp", "КПП")}
            {field("ogrn", "ОГРН / ОГРНИП")}
            {field("legal_address", "Юридический адрес", { wide: true })}
            {field("postal_address", "Почтовый адрес", { wide: true })}
            {field("email", "Электронная почта", { type: "email" })}
            {field("phone", "Телефон")}
            {field("signer_name", "ФИО подписанта")}
            {field("signer_position", "Должность подписанта")}
            {field("signer_basis", "Действует на основании", { placeholder: "Устава" })}
          </Row>

          <H5 className="mt-3 mb-2">Банковские реквизиты</H5>
          <P className="text-muted">
            Обязательны, если по договору деньги идут в вашу сторону: для владельца
            локации и для партнёра.
          </P>
          <Row>
            {field("bank_name", "Банк", { wide: true })}
            {field("bank_bik", "БИК")}
            {field("bank_account", "Расчётный счёт")}
            {field("bank_corr_account", "Корреспондентский счёт")}
          </Row>

          {error && <P className="text-danger">{error}</P>}
          {message && <P className="text-success">{message}</P>}

          <Btn attrBtn={{ color: "primary", type: "submit", disabled: saving }}>
            {saving ? "Сохранение…" : "Сохранить реквизиты"}
          </Btn>
        </Form>
      </CardBody>
    </Card>
  );
};

export default RequisitesForm;
